import React from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import includes from "lodash/includes";

import { ProgressIndicator } from "@eventbrite/valkyrie-eds-progress-indicator";
import { Icon } from "@eventbrite/valkyrie-eds-icon";
import { CheckChunky, CrossChunky } from "@eventbrite/valkyrie-eds-iconography";

import * as constants from "./constants";
import { isIconComponent } from "./utils";

import styles from "./Button.module.scss";

export type BaseButtonProps = {
  /**
   * Defines the type of button. (button, submit, or link)
   */
  type: (typeof constants.TYPES)[number];
  /**
   * Changes the style of the button.
   * Available styles: none, anchor, link, neutral, fill, follow
   */
  style: (typeof constants.STYLES)[number];
  /**
   * Changes button size
   * Available sizes: responsive, block
   */
  size?: (typeof constants.SIZES)[number];
  /**
   * Set active styles to button
   */
  isFollowing?: boolean;
  /**
   * Adds a loading spinner to button
   */
  isLoading?: boolean;
  /**
   * Adds a checkmark to button
   */
  isSuccessful?: boolean;
  /**
   * Adds a cross to button
   */
  isNotSuccessful?: boolean;
  /**
   * Callback function invoked when the button is clicked.
   * () => { }
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * Disables the button
   */
  disabled?: boolean;
  /**
   * Adds disabled style to button
   */
  keepDisabledStyleWhileLoading?: boolean;
  /**
   * is a phab the button
   */
  phab?: boolean;
  /**
   * Container class name
   */
  __containerClassName?: string;
  /**
   * Sets the button to full width.
   */
  isFullWidth?: boolean;
};

type ButtonAsButtonProps = BaseButtonProps &
  React.ComponentPropsWithoutRef<"button"> & {
    type: "button" | "submit";
  };

type ButtonAsLinkProps = BaseButtonProps &
  React.ComponentPropsWithoutRef<"a"> & {
    type: "link";
  };

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

interface ButtonBodyProps {
  isLoading?: boolean;
  isSuccessful?: boolean;
  isNotSuccessful?: boolean;
  style: keyof typeof constants.BUTTON_PROGRESS_STYLE;
  children?: React.ReactNode;
}

const ButtonButton = ({
  children,
  ...componentAttrs
}: {
  children: React.ReactNode;
  [key: string]: any;
}) => (
  <button {...componentAttrs} type="button">
    {children}
  </button>
);

const ButtonSubmit = ({
  children,
  ...componentAttrs
}: {
  children: React.ReactNode;
  [key: string]: any;
}) => (
  <button {...componentAttrs} type="submit">
    {children}
  </button>
);

const ButtonLink = ({
  children,
  ...componentAttrs
}: {
  children: React.ReactNode;
  [key: string]: any;
}) => <a {...componentAttrs}>{children}</a>;

interface LoadingProps {
  className: string;
  style: keyof typeof constants.BUTTON_PROGRESS_STYLE;
}

const Loading = ({ className, style }: LoadingProps) => (
  <div className={className}>
    <ProgressIndicator
      shape="circular"
      size="small-chunky"
      style={constants.BUTTON_PROGRESS_STYLE[style]}
      className={styles.progressIndicator}
    />
  </div>
);

const Success = ({ className }: { className: string }) => (
  <span className={className}>
    <Icon
      size="small"
      type={<CheckChunky />}
      animated={true}
      __containerClassName={styles.icon}
    />
  </span>
);

const Fail = ({ className }: { className: string }) => (
  <i className={className}>
    <Icon
      size="small"
      type={<CrossChunky />}
      __containerClassName={styles.icon}
    />
  </i>
);

const TYPES_COMPONENT_MAP = {
  [constants.TYPE_BUTTON]: ButtonButton,
  [constants.TYPE_SUBMIT]: ButtonSubmit,
  [constants.TYPE_LINK]: ButtonLink,
};
const ButtonBody = ({
  isLoading,
  isSuccessful,
  isNotSuccessful,
  children,
  style,
}: ButtonBodyProps) => {
  let component;
  const className = classNames(styles.children, {
    [styles.loading as string]: isLoading,
  });
  const componentClassName = classNames(styles.extra, {
    [styles.delay as string]: isLoading || isNotSuccessful,
  });

  if (isLoading) {
    component = <Loading className={componentClassName} style={style} />;
  }

  if (isSuccessful) {
    component = <Success className={componentClassName} />;
  }

  if (isNotSuccessful) {
    component = <Fail className={componentClassName} />;
  }

  return (
    <div>
      <div className={className}>{children}</div>
      {component}
    </div>
  );
};

export class Button extends React.PureComponent<ButtonProps> {
  static defaultProps = {
    type: constants.TYPE_BUTTON,
    style: constants.STYLE_NEUTRAL,
  };

  _handleClick(syntheticEvent: React.MouseEvent<HTMLElement, MouseEvent>) {
    syntheticEvent.stopPropagation();
    // simple wrapper for onClick so that the event object doesn't
    // get passed to the callback

    if (this.props.onClick) {
      syntheticEvent.persist();
      if (this.props.type === constants.TYPE_LINK) {
        syntheticEvent.preventDefault();
      }
      this.props.onClick(syntheticEvent);
    }
  }

  focus() {
    // TODO: This implementation should be revisited so that we hopefully
    // don't need to do this
    // eslint-disable-next-line react/no-find-dom-node
    (ReactDOM.findDOMNode(this) as HTMLElement)?.focus();
  }

  render() {
    const {
      children,
      isFollowing,
      isLoading,
      keepDisabledStyleWhileLoading,
      isSuccessful,
      isNotSuccessful,
      isFullWidth,
      phab,
      size,
      style,
      type,
      __containerClassName,
      ...extraAttrs
    } = this.props;
    let { disabled } = this.props;

    const componentChildren = React.Children.toArray(children);
    const childCount = componentChildren.length;
    const iconCount = componentChildren.filter(isIconComponent).length;
    const hasIconsOnly = childCount === iconCount;
    const hasIconsAndOtherChildren =
      childCount - iconCount > 0 && iconCount > 0;
    const className = getButtonClassNames({
      disabled,
      hasIconsAndOtherChildren,
      hasIconsOnly,
      isFollowing,
      isLoading,
      keepDisabledStyleWhileLoading,
      isSuccessful,
      isNotSuccessful,
      isFullWidth,
      phab,
      size,
      style,
      type,
      __containerClassName,
    });

    let onClick;
    let accessibilityProps = {};

    if (disabled || isLoading || isSuccessful || isNotSuccessful) {
      accessibilityProps = {
        ...accessibilityProps,
        "aria-disabled": true,
      };
      disabled = true;
    } else {
      // set click handler if not disabled because
      // anchor-type buttons don't respect disabled attribute
      onClick = this._handleClick.bind(this);
    }

    const componentAttrs = {
      ...extraAttrs,
      className,
      disabled,
      onClick,
    };

    const ButtonSubComponent = TYPES_COMPONENT_MAP[type];

    let buttonBody = children;

    // ButtonBody provides an inner container for showing loading, success, and error states
    // that is otherwise not needed (and in fact, may break components like ExpandableCardContent
    // that depend on children to be the direct descendants of ButtonSubComponent)
    if (isLoading || isSuccessful || isNotSuccessful) {
      buttonBody = (
        <ButtonBody
          style={keepDisabledStyleWhileLoading ? constants.STYLE_NONE : style}
          isLoading={isLoading}
          isSuccessful={isSuccessful}
          isNotSuccessful={isNotSuccessful}
        >
          {children}
        </ButtonBody>
      );
    }

    return (
      <ButtonSubComponent {...componentAttrs} {...accessibilityProps}>
        {buttonBody}
      </ButtonSubComponent>
    );
  }
}

export const getButtonClassNames = ({
  type,
  style,
  size,
  isFollowing,
  isLoading,
  isSuccessful,
  isNotSuccessful,
  isFullWidth,
  disabled,
  phab,
  hasIconsOnly,
  hasIconsAndOtherChildren,
  keepDisabledStyleWhileLoading,
  __containerClassName,
}) => {
  const className = classNames(
    {
      [styles.root as string]: !includes(
        [constants.STYLE_ANCHOR, constants.STYLE_NONE],
        style as string,
      ),
      [styles[type as string] as string]: type,
      [styles[style as string] as string]: style,
      [styles[size as string] as string]: size,
      [styles.following as string]: isFollowing,
      [styles.loading as string]: isLoading,
      [styles.successful as string]: isSuccessful,
      [styles.notSuccessful as string]: isNotSuccessful,
      [styles.disabled as string]:
        disabled && (keepDisabledStyleWhileLoading || !isLoading),
      [styles.phab as string]: phab,
      "eds-link": style === constants.STYLE_ANCHOR, // TODO: refactor out eds-link global style
      [styles.iconOnly as string]: hasIconsOnly,
      [styles.iconWithOtherChildren as string]: hasIconsAndOtherChildren,
      [styles.fullWidth as string]: isFullWidth,
    },
    __containerClassName,
  );

  return className;
};
